import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface CurrencyInfo {
    code: string;
    name: string;
    symbol: string;
    isActive: boolean;
    isBase: boolean;
}

interface CurrencyContextType {
    currencies: CurrencyInfo[];
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

// Fallback currencies if API hasn't loaded yet
const FALLBACK_CURRENCIES: CurrencyInfo[] = [
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', isActive: true, isBase: true },
    { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨', isActive: true, isBase: false },
    { code: 'USD', name: 'US Dollar', symbol: '$', isActive: true, isBase: false },
    { code: 'GBP', name: 'British Pound', symbol: '£', isActive: true, isBase: false },
];

const CurrencyContext = createContext<CurrencyContextType>({
    currencies: FALLBACK_CURRENCIES,
    loading: false,
    error: null,
    refetch: async () => { },
});

export const useCurrencies = () => useContext(CurrencyContext);

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currencies, setCurrencies] = useState<CurrencyInfo[]>(FALLBACK_CURRENCIES);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchCurrencies = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('token');
            const res = await fetch('/api/accounting/currencies?isActive=true', {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                credentials: 'include',
            });
            if (res.ok) {
                const data = await res.json();
                if (data.length > 0) {
                    setCurrencies(data);
                }
            }
        } catch (err: any) {
            console.error('Failed to fetch currencies:', err);
            setError(err.message || 'Failed to fetch currencies');
            // Keep fallback currencies on error
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCurrencies();
    }, [fetchCurrencies]);

    return (
        <CurrencyContext.Provider value={{ currencies, loading, error, refetch: fetchCurrencies }}>
            {children}
        </CurrencyContext.Provider>
    );
};

export default CurrencyContext;
